import { ReactNode, useCallback, useEffect } from 'react';

import { useClientBalance } from './useClientBalance';
import { ClientBalanceContext } from './clientBalanceContext';
import { useUpdateCreditsHistory } from './credits-history/hooks';
import { CheckIfUserHasPortalPrivilege } from '../../../common/client-status/interfaces';

interface Props {
  children: ReactNode;
  checkIfUserHasPortalPrivilege: CheckIfUserHasPortalPrivilege;
}

export const WithClientBalance = ({ children, checkIfUserHasPortalPrivilege }: Props) => {
  const { isClientBalanceError, updateClientBalance, getClientBalance } = useClientBalance();
  const {
    updateCreditsHistory,
    creditsHistory,
    creditsHistoryError,
    creditsHistoryLoading
  } = useUpdateCreditsHistory();

  const shouldUpdateBalance = useCallback(() => checkIfUserHasPortalPrivilege('BALANCE_CHECK'), [
    checkIfUserHasPortalPrivilege
  ]);

  const updateBalance: () => Promise<void> = useCallback(
    () => (shouldUpdateBalance() ? updateClientBalance() : Promise.resolve()),
    [shouldUpdateBalance, updateClientBalance]
  );

  useEffect(() => {
    updateBalance(); // Load balance on init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // @ts-ignore
  useEffect(() => {
    if (shouldUpdateBalance()) {
      const clientBalanceInterval = setInterval(
        isClientBalanceError ? () => {} : updateBalance,
        window.nortada.nortadaSettings.clientBalanceRefreshTime
      );

      return () => {
        clearInterval(clientBalanceInterval);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
    return () => {};
  }, [isClientBalanceError, shouldUpdateBalance, updateBalance]);

  return (
    <ClientBalanceContext.Provider
      value={{
        updateClientBalance: updateBalance,
        getClientBalance,
        updateCreditsHistory,
        creditsHistory,
        creditsHistoryError,
        creditsHistoryLoading
      }}
    >
      {children}
    </ClientBalanceContext.Provider>
  );
};
